import { Directive, Input, TemplateRef, ViewContainerRef, OnInit } from '@angular/core';
import { SettingsManagerService } from 'src/app/services/settings-manager.service';

/**
 * Structural directive to control visibility of settings tabs based on feature flags.
 *
 * Usage:
 *   <mat-tab *appSettingsTabVisible="TabId.WEBHOOKS">...</mat-tab>
 *
 * The tab will be completely removed from DOM (not rendered at all) if the
 * corresponding feature flag variable is set to false in Optimizely.
 * This provides security by ensuring hidden tabs are not accessible in browser dev tools.
 *
 * Note: Feature flags are guaranteed to be loaded before settings panel is opened,
 * so we can check visibility synchronously without subscriptions.
 */
@Directive({
  selector: '[appSettingsTabVisible]',
  standalone: true,
})
export class SettingsTabVisibleDirective implements OnInit {
  private tabKey!: string;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private settingsManager: SettingsManagerService,
  ) {}

  /**
   * Sets the tab key to control visibility.
   * @param tabKey - Tab identifier checked by SettingsManagerService.isTabHidden
   *                 (Optimizely and/or a mapped capability).
   *
   * Note: If hidden, the element is not rendered in DOM at all for security.
   */
  @Input() set appSettingsTabVisible(tabKey: string) {
    this.tabKey = tabKey;
  }

  ngOnInit(): void {
    // Check visibility once - feature flags don't change after page load
    const isHidden = this.settingsManager.isTabHidden(this.tabKey);

    if (!isHidden) {
      // Render the tab in DOM
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
    // If hidden, viewContainer remains empty (element not rendered in DOM)
  }
}
